import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { FileText, Download, CheckCircle, Upload, DollarSign, Calendar, Shield } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import * as api from '../lib/api';

const TaxesPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('w9');
  const [w9Status, setW9Status] = useState('pending');
  const [taxDocs, setTaxDocs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ ytd_earnings: '$0', tax_withheld: '$0', estimated_tax: '$0', next_payment: '-' });
  const [formData, setFormData] = useState({ legal_name: '', business_name: '', tax_classification: 'individual', address: '', city: '', state: '', zip_code: '', ssn: '', ein: '', signature: false });

  useEffect(() => {
    const load = async () => {
      try {
        const w9Data = await api.getW9();
        setW9Status(w9Data.status || 'pending');
        if (w9Data.form_data) setFormData(prev => ({ ...prev, ...w9Data.form_data }));
      } catch {}
      try {
        const docsData = await api.getTaxDocuments();
        setTaxDocs(docsData.documents || []);
      } catch {
        setTaxDocs([{ id: 1, name: '1099-NEC (2024)', description: 'Nonemployee Compensation', amount: '$45,200', status: 'Available', date: 'January 31, 2025' }]);
      }
      try {
        const payData = await api.getTaxPayments();
        setPayments(payData.payments || []);
        setSummary(payData.summary || summary);
      } catch {
        setSummary({ ytd_earnings: '$8,450', tax_withheld: '$0', estimated_tax: '$2,113', next_payment: 'April 15, 2025' });
      }
    };
    load();
  }, [user]);

  const handleW9Submit = async () => {
    if (!formData.legal_name || !formData.address || !formData.signature) {
      toast({ title: 'Incomplete form', description: 'Please fill in all required fields.', variant: 'destructive' }); return;
    }
    try { await api.submitW9(formData); } catch {}
    setW9Status('submitted');
    toast({ title: 'W-9 submitted successfully', description: "Your W-9 form has been submitted for review." });
  };

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-5xl mx-auto px-8 py-12">
          <div className="mb-8"><h1 className="text-3xl font-bold text-gray-900 mb-2">Taxes & W-9</h1><p className="text-gray-600">Manage your tax documents and filing information</p></div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6"><DollarSign className="w-6 h-6 text-green-600 mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{summary.ytd_earnings}</div><div className="text-sm text-gray-600">YTD Earnings</div></Card>
            <Card className="p-6"><DollarSign className="w-6 h-6 text-red-600 mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{summary.tax_withheld}</div><div className="text-sm text-gray-600">Tax Withheld</div></Card>
            <Card className="p-6"><DollarSign className="w-6 h-6 text-orange-600 mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{summary.estimated_tax}</div><div className="text-sm text-gray-600">Estimated Tax Due</div></Card>
            <Card className="p-6"><Calendar className="w-6 h-6 text-[#0A3900] mb-2" /><div className="text-lg font-bold text-gray-900 mb-1">{summary.next_payment}</div><div className="text-sm text-gray-600">Next Payment Due</div></Card>
          </div>

          <div className="flex gap-2 mb-6">
            {[{ key: 'w9', label: 'W-9 Form' }, { key: 'documents', label: 'Tax Documents' }, { key: 'history', label: 'Payment History' }].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-[#0A3900] text-white' : 'bg-white text-gray-700 border hover:bg-gray-50'}`}>{tab.label}</button>
            ))}
          </div>

          {activeTab === 'w9' && (
            <Card className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div><h2 className="text-2xl font-bold text-gray-900">Form W-9</h2><p className="text-gray-600">Request for Taxpayer Identification Number and Certification</p></div>
                <Badge className={w9Status === 'approved' ? 'bg-green-100 text-green-800' : w9Status === 'submitted' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}>
                  {w9Status === 'approved' ? 'Approved' : w9Status === 'submitted' ? 'Under Review' : 'Not Submitted'}
                </Badge>
              </div>
              {w9Status === 'submitted' ? (
                <div className="text-center py-12"><Shield className="w-16 h-16 text-yellow-500 mx-auto mb-4" /><h3 className="text-xl font-semibold text-gray-900 mb-2">W-9 Under Review</h3><p className="text-gray-600 mb-4">This typically takes 1-2 business days.</p><Button variant="outline" onClick={() => setW9Status('pending')}>Re-submit W-9</Button></div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><Label>Legal Name *</Label><Input className="mt-2" value={formData.legal_name} onChange={(e) => setFormData({...formData, legal_name: e.target.value})} placeholder="John Doe" /></div>
                    <div><Label>Business Name (if different)</Label><Input className="mt-2" value={formData.business_name} onChange={(e) => setFormData({...formData, business_name: e.target.value})} placeholder="Doe Consulting LLC" /></div>
                  </div>
                  <div><Label>Federal Tax Classification *</Label><select className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md" value={formData.tax_classification} onChange={(e) => setFormData({...formData, tax_classification: e.target.value})}>
                    <option value="individual">Individual/Sole Proprietor</option><option value="c-corp">C Corporation</option><option value="s-corp">S Corporation</option><option value="partnership">Partnership</option><option value="llc">LLC</option>
                  </select></div>
                  <div><Label>Street Address *</Label><Input className="mt-2" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="123 Main St" /></div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div><Label>City *</Label><Input className="mt-2" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} placeholder="San Francisco" /></div>
                    <div><Label>State *</Label><Input className="mt-2" value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} placeholder="CA" /></div>
                    <div><Label>ZIP Code *</Label><Input className="mt-2" value={formData.zip_code} onChange={(e) => setFormData({...formData, zip_code: e.target.value})} placeholder="94105" /></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><Label>SSN</Label><Input className="mt-2" type="password" value={formData.ssn} onChange={(e) => setFormData({...formData, ssn: e.target.value})} placeholder="XXX-XX-XXXX" /></div>
                    <div><Label>EIN</Label><Input className="mt-2" value={formData.ein} onChange={(e) => setFormData({...formData, ein: e.target.value})} placeholder="XX-XXXXXXX" /></div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg"><div className="flex items-start gap-3">
                    <input type="checkbox" id="w9-sig" className="mt-1 w-4 h-4" checked={formData.signature} onChange={(e) => setFormData({...formData, signature: e.target.checked})} />
                    <Label htmlFor="w9-sig" className="text-sm text-gray-700">Under penalties of perjury, I certify that the information provided is correct. I am electronically signing this W-9 form.</Label>
                  </div></div>
                  <div className="flex gap-3 pt-4"><Button onClick={handleW9Submit} className="bg-[#0A3900] hover:bg-[#1A5C0A]"><Upload className="w-4 h-4 mr-2" />Submit W-9</Button><Button variant="outline"><Download className="w-4 h-4 mr-2" />Download Blank W-9</Button></div>
                </div>
              )}
            </Card>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-4">
              {taxDocs.map(doc => (
                <Card key={doc.id} className="p-6"><div className="flex items-center justify-between">
                  <div className="flex items-center gap-4"><div className="p-3 bg-[#E0E8D0] rounded-lg"><FileText className="w-6 h-6 text-[#0A3900]" /></div>
                    <div><h3 className="text-lg font-semibold text-gray-900">{doc.name}</h3><p className="text-sm text-gray-600">{doc.description} - {doc.amount}</p><p className="text-sm text-gray-500">{doc.date}</p></div></div>
                  <div className="flex items-center gap-3"><Badge className={doc.status === 'Available' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{doc.status}</Badge><Button variant="outline" size="sm"><Download className="w-4 h-4 mr-2" />Download</Button></div>
                </div></Card>
              ))}
            </div>
          )}

          {activeTab === 'history' && (
            <Card><div className="p-6 border-b"><h2 className="text-xl font-semibold text-gray-900">Payment History (Tax Year 2025)</h2></div>
              <div className="overflow-x-auto"><table className="w-full">
                <thead className="bg-gray-50 border-b"><tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr></thead>
                <tbody className="divide-y">{payments.map((p, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{p.date}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{p.description}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{p.amount}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{p.category}</td>
                    <td className="px-6 py-4"><span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">{p.status}</span></td>
                  </tr>
                ))}</tbody>
              </table></div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaxesPage;
